// src/pages/auth/Login.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Form,
  Heading,
  Button,
  Anchor,
  Image,
  Text,
} from "../../components/elements";
import Logo from "../../components/Logo";
import data from "../../data/master/login.json";
import Axios from "axios";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { Link, useNavigate } from "react-router-dom";

export default function Login() {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await Axios.get(
          `${process.env.REACT_APP_BASE_URL}common/companies/active`
        );
        setCompanies(res.data.companies || []);

        // Check for saved company in cookie
        const savedCompanyId = getCookie("selectedCompany");
        if (savedCompanyId) {
          setSelectedCompany(savedCompanyId);
        }
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch companies:", error);
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);

  // Cookie helper functions
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault(); // Prevent form submission reload

    setError("");
    if (!email || !password) {
      setError("Please provide email and password.");
      return;
    }

    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_BASE_URL}common/login`,
        {
          email,
          password,
          companyId: selectedCompany || undefined
        }
      );

      // Save token
      localStorage.setItem("token", res.data.token);

      // Save user data based on type
      if (res.data.userType === "master_admin") {
        localStorage.setItem("userType", "master_admin");
        localStorage.setItem("admin", JSON.stringify(res.data.admin));
      } else if (res.data.userType === "admin") {
        localStorage.setItem("userType", "admin");
        localStorage.setItem("admin", JSON.stringify(res.data.admin));
        // Save company preference for company admins
        if (selectedCompany) {
          setCookie("selectedCompany", selectedCompany);
        }
      } else if (res.data.userType === "user") {
        localStorage.setItem("userType", "user");
        localStorage.setItem("user", JSON.stringify(res.data.user));
        // Save company preference
        if (selectedCompany) {
          setCookie("selectedCompany", selectedCompany);
        }
      }

      // redirect - force page reload to trigger auth check
      window.location.href = "/";
    } catch (e) {
      setError(e.response?.data?.message || "Login failed");
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin(e);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("token")) {
      navigate("/");
    }
  }, [navigate]);

  return (
    <Box
      className="mc-auth"
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Image
        src={data?.pattern.src}
        alt={data?.pattern.alt}
        className="mc-auth-pattern"
      />
      <Box className="mc-auth-group">
        <Logo
          src={data?.logo.src}
          alt={data?.logo.alt}
          href={data?.logo.path}
          className="mc-auth-logo"
        />
        <Heading as="h4" className="mc-auth-title">
          {data?.title}
        </Heading>

        <Form className="mc-auth-form" onSubmit={handleLogin}>
          {/* Select Company */}
          {loading ? (
            <Text style={{ marginBottom: "1.5rem" }}>Loading companies...</Text>
          ) : companies.length > 0 ? (
            <TextField
              select
              label="Select Company"
              variant="filled"
              fullWidth
              sx={{ mb: "1.5rem" }}
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              helperText="Optional for admins, required for users"
              onKeyPress={handleKeyPress}
            >
              <MenuItem value="">
                <em>None (Admin only)</em>
              </MenuItem>
              {companies.map((company) => (
                <MenuItem key={company._id} value={company._id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          ) : null}

          {/* Email */}
          <TextField
            label="Email"
            type="email"
            variant="filled"
            fullWidth
            sx={{ mb: "1.5rem" }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
          />

          {/* Password */}
          <TextField
            label="Password"
            type="password"
            variant="filled"
            fullWidth
            sx={{ mb: "1.5rem" }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
          />

          {error && <Text className="mc-alert red">{error}</Text>}

          <Button
            onClick={handleLogin}
            className={`mc-auth-btn ${data?.button.fieldSize}`}
            type={data?.button.type}
          >
            {data?.button.text}
          </Button>

          <Anchor className="mc-auth-forgot" href={data?.forgot.path}>
            {data?.forgot.text}
          </Anchor>
        </Form>
      </Box>
    </Box>
  );
}
